import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormColumns } from "components/form/FormColumns/FormColumns";
import { FormField } from "components/form/FormField/FormField";
import { CustomInput } from "components/helpers/CustomInput/CustomInput";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { FileUploadZone } from "components/ui/FileUploadZone";
import { PatientAutocomplete } from "components/ui/PatientAutocomplete/PatientAutocomplete";
import { Select } from "components/ui/Select/Select";
import { useDocumentTypes } from "features/documents/hooks/useDocumentTypes";
import { useDocumentUpload } from "features/documents/hooks/useDocumentUpload";
import { useStaffMembers } from "features/practice/hooks/useStaffMembers";
import { TaskPriority } from "utils/Constant";
import { formatPatientWithDateOfBirth } from "utils/dateFormatter";
import { onInvalidSubmit } from "utils/reportFormErrors";

import styles from "./UploadDocumentModal.module.css";

const statusOptions = [
  { value: "NEW", label: "New" },
  { value: "REFERRED", label: "Referred" },
  { value: "IN_REVIEW", label: "In Review" },
  { value: "REVIEWED", label: "Reviewed" },
  { value: "SYSTEM_GENERATED", label: "System Generated" },
];

const formatTypeLabel = (name) => {
  if (!name) return "";
  return name
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formSchema = z.object({
  name: z.string().min(1, "Document name is required"),
  documentType: z
    .string()
    .refine((val) => val !== "" && val !== "undefined", "Document type is required"),
  otherDocumentType: z.string().optional(),
  status: z.string().min(1, "Status is required"),
  staffMemberId: z.string().optional(),
  priority: z
    .string()
    .refine(
      (val) => val !== "-1" && val !== "" && val !== "undefined",
      "Priority is required"
    ),
});

export const UploadDocumentModal = ({
  isOpen,
  onClose,
  patient,
  onSuccess,
  allowPatientSelection = false,
  documentType: preselectedTypeName,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientError, setPatientError] = useState("");

  const { data: documentTypes = [], isLoading: isLoadingTypes } = useDocumentTypes();
  const { data: staffMembers = [] } = useStaffMembers();
  const { mutate: uploadDocument, isPending } = useDocumentUpload({
    onSuccess: () => {
      setSelectedFile(null);
      setFileError("");
      onSuccess?.();
      onClose();
    },
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      documentType: "",
      otherDocumentType: "",
      status: "NEW",
      staffMemberId: patient?.doctorDetails?.id?.toString() || "",
      priority: "1",
    },
  });

  useEffect(() => {
    if (isOpen) {
      const matchingType =
        preselectedTypeName && preselectedTypeName !== "ALL"
          ? documentTypes.find((dt) => dt.name === preselectedTypeName)
          : null;

      form.reset({
        name: "",
        documentType: matchingType ? String(matchingType.id ?? "") : "",
        otherDocumentType: "",
        status: "NEW",
        staffMemberId: patient?.doctorDetails?.id?.toString() || "",
        priority: "1",
      });
      setSelectedFile(null);
      setFileError("");
      setSelectedPatient(null);
      setPatientError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    if (file) setFileError("");
  };

  const handleClose = () => {
    form.reset();
    setSelectedFile(null);
    setFileError("");
    setSelectedPatient(null);
    setPatientError("");
    onClose();
  };

  const onSubmit = (values) => {
    if (!selectedFile) {
      setFileError("Please upload a file");
      return;
    }

    if (allowPatientSelection && !selectedPatient?.id) {
      setPatientError("Please select a patient");
      return;
    }

    const patientId = allowPatientSelection
      ? selectedPatient?.id
      : patient?.id || patient?.patient_id;

    uploadDocument({
      imageFile: selectedFile,
      formData: { ...values, patientId },
      patient: { id: patientId, patient_id: patientId },
    });
  };

  const handleSubmit = form.handleSubmit(onSubmit, onInvalidSubmit("UploadDocumentModal"));
  const watchedDocumentType = form.watch("documentType");
  const showOtherType = watchedDocumentType === "Other";

  const patientDisplayValue = formatPatientWithDateOfBirth(patient);

  return (
    <Dialog open={isOpen} onClose={handleClose} title="Add new document" width={780}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div>
            <FileUploadZone
              onFileSelect={handleFileSelect}
              selectedFile={selectedFile}
              hasError={!!fileError}
            />
            {!!fileError && <p className={styles.fileError}>{fileError}</p>}
          </div>

          <FormColumns columns={2} gap={24}>
            <FormField name="name" label="Document name">
              <CustomInput {...form.register("name")} />
            </FormField>
            <FormField name="documentType" label="Type">
              <Controller
                name="documentType"
                control={form.control}
                render={({ field }) => (
                  <Select
                    options={documentTypes.map((dt) => ({
                      label: formatTypeLabel(dt.name),
                      value: String(dt.id ?? ""),
                    }))}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      if (value !== "Other") {
                        form.setValue("otherDocumentType", "");
                      }
                    }}
                    placeholder={isLoadingTypes ? "Loading..." : "Select"}
                    disabled={isLoadingTypes}
                    fullWidth
                  />
                )}
              />
            </FormField>
          </FormColumns>

          {showOtherType && (
            <FormField name="otherDocumentType" label="Other document type">
              <CustomInput
                {...form.register("otherDocumentType")}
                placeholder="Enter document type"
              />
            </FormField>
          )}

          <FormColumns columns={2} gap={24}>
            <FormField name="status" label="Status">
              <Controller
                name="status"
                control={form.control}
                render={({ field }) => (
                  <Select
                    options={statusOptions}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select"
                    fullWidth
                  />
                )}
              />
            </FormField>
            <FormField name="priority" label="Priority">
              <Controller
                name="priority"
                control={form.control}
                render={({ field }) => (
                  <Select
                    options={TaskPriority.map((p) => ({
                      label: p.name,
                      value: String(p.value),
                    }))}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select"
                    fullWidth
                  />
                )}
              />
            </FormField>
          </FormColumns>

          <FormColumns columns={2} gap={24}>
            <FormField name="staffMemberId" label="Reviewer">
              <Controller
                name="staffMemberId"
                control={form.control}
                render={({ field }) => (
                  <Select
                    options={staffMembers.map((staff) => ({
                      label: `${staff.active ? "✅ " : ""}${staff.firstName} ${staff.lastName}${staff.staffRole ? ` (${staff.staffRole.toLowerCase()})` : ""}`,
                      value: String(staff.id),
                    }))}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select"
                    fullWidth
                  />
                )}
              />
            </FormField>
            <FormField name="patient" label="Patient">
              {allowPatientSelection ? (
                <>
                  <PatientAutocomplete
                    id="patient-select"
                    value={selectedPatient}
                    onChange={(newValue) => {
                      setSelectedPatient(newValue);
                      if (patientError) setPatientError("");
                    }}
                    placeholder="Search patient..."
                  />
                  {!!patientError && (
                    <p className={styles.patientError}>{patientError}</p>
                  )}
                </>
              ) : (
                <CustomInput value={patientDisplayValue} disabled />
              )}
            </FormField>
          </FormColumns>
        </form>
      </FormProvider>
      <DialogFooter>
        <Button variant="outline" onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isPending}>
          {isPending ? "Saving..." : "Save"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
